/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from 'react'

import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import Skeleton from '@mui/material/Skeleton'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import Autocomplete from '@mui/material/Autocomplete'
import Tooltip from '@mui/material/Tooltip'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
// eslint-disable-next-line import/no-duplicates
import ptBR from 'date-fns/locale/pt-BR'

import { useBoolean } from 'src/hooks/use-boolean'
import useDebounce from 'src/hooks/use-debounce'
import Iconify from 'src/components/iconify'
import Scrollbar from 'src/components/scrollbar'
import DateTimeRangePicker from 'src/components/date-time-range-picker'
import { useSnackbar } from 'src/components/snackbar'
import {
  COST_CATEGORY_LABELS,
  FinanceCost,
  FinanceCostCategory,
  FinanceCostPayload,
  FinanceEvent,
  createCost,
  deleteCost,
  fetchCosts,
  fetchFinanceEvents,
  updateCost,
} from 'src/service/finance.service'

import { formatCurrency } from '../orders/format-currency'

const fmtDate = (value?: string | Date | null) => {
  if (!value) return '—'
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }).format(new Date(value))
}

const parseDecimal = (value: string) => {
  const parsed = Number(String(value).replace(/\./g, '').replace(',', '.'))
  return Number.isNaN(parsed) ? 0 : parsed
}

type EventOption = { value: string; label: string }

type FormState = {
  description: string
  value: string
  date: Date | null
  category: FinanceCostCategory
  event: EventOption | null
  notes: string
}

const emptyForm: FormState = {
  description: '',
  value: '',
  date: new Date(),
  category: 'other',
  event: null,
  notes: '',
}

export default function CostsTab() {
  const { enqueueSnackbar } = useSnackbar()
  const isLoading = useBoolean(true)
  const isSaving = useBoolean(false)

  const [costs, setCosts] = useState<FinanceCost[]>([])
  const [total, setTotal] = useState(0)
  const [totalValue, setTotalValue] = useState(0)
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(10)

  // filtros
  const [eventFilter, setEventFilter] = useState<EventOption | null>(null)
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)

  // opções de eventos (compartilhadas entre filtro e formulário)
  const [eventSearch, setEventSearch] = useState('')
  const [eventOptions, setEventOptions] = useState<EventOption[]>([])
  const eventSearchDebounce = useDebounce(eventSearch, 400)

  const [editing, setEditing] = useState<FinanceCost | null>(null)
  const [form, setForm] = useState<FormState | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      isLoading.onTrue()
      const data = await fetchCosts({
        event: eventFilter?.value || undefined,
        category: (categoryFilter as FinanceCostCategory) || undefined,
        startDate,
        endDate,
        page: page + 1,
        perPage,
      })
      setCosts(data.costs)
      setTotal(data.total)
      setTotalValue(data.totalValue)
    } catch {
      setCosts([])
      setTotal(0)
      setTotalValue(0)
    } finally {
      isLoading.onFalse()
    }
  }, [eventFilter, categoryFilter, startDate, endDate, page, perPage])

  useEffect(() => {
    load()
  }, [eventFilter, categoryFilter, startDate, endDate, page, perPage])

  useEffect(() => {
    setPage(0)
  }, [eventFilter, categoryFilter, startDate, endDate])

  useEffect(() => {
    let ignore = false
    fetchFinanceEvents(eventSearchDebounce).then((events: FinanceEvent[]) => {
      if (ignore) return
      setEventOptions(events.map((e) => ({ value: e._id, label: e.name })))
    })
    return () => {
      ignore = true
    }
  }, [eventSearchDebounce])

  const openCreate = () => {
    setEditing(null)
    setForm({ ...emptyForm, date: new Date() })
  }

  const openEdit = (cost: FinanceCost) => {
    setEditing(cost)
    setForm({
      description: cost.description,
      value: String(cost.value / 100).replace('.', ','),
      date: cost.date ? new Date(cost.date) : new Date(),
      category: cost.category,
      event: cost.event ? { value: cost.event._id, label: cost.event.name } : null,
      notes: cost.notes || '',
    })
  }

  const handleSave = async () => {
    if (!form) return
    if (!form.description.trim()) {
      enqueueSnackbar('Informe a descrição do custo.', { variant: 'warning' })
      return
    }
    const valueInCents = Math.round(parseDecimal(form.value) * 100)
    if (valueInCents <= 0) {
      enqueueSnackbar('Informe um valor válido.', { variant: 'warning' })
      return
    }

    const payload: FinanceCostPayload = {
      description: form.description.trim(),
      value: valueInCents,
      date: form.date || new Date(),
      category: form.category,
      event: form.event?.value || null,
      notes: form.notes,
    }

    try {
      isSaving.onTrue()
      if (editing) {
        await updateCost(editing._id, payload)
        enqueueSnackbar('Custo atualizado!')
      } else {
        await createCost(payload)
        enqueueSnackbar('Custo lançado!')
      }
      setForm(null)
      setEditing(null)
      await load()
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.error || 'Erro ao salvar custo.', {
        variant: 'error',
      })
    } finally {
      isSaving.onFalse()
    }
  }

  const handleDelete = async (cost: FinanceCost) => {
    try {
      setDeletingId(cost._id)
      await deleteCost(cost._id)
      enqueueSnackbar('Custo removido.')
      await load()
    } catch {
      enqueueSnackbar('Erro ao remover custo.', { variant: 'error' })
    } finally {
      setDeletingId(null)
    }
  }

  const eventFilterOptions: EventOption[] = [
    { value: 'general', label: 'Custos gerais (sem evento)' },
    ...eventOptions,
  ]

  return (
    <>
      <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent='space-between'
          spacing={1.5}
          sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
        >
          <Stack direction='row' alignItems='center' spacing={1.25}>
            <Iconify icon='solar:banknote-bold' width={18} sx={{ color: 'primary.main' }} />
            <Typography variant='subtitle2' fontWeight={700}>
              Custos lançados
            </Typography>
            <Chip
              size='small'
              variant='soft'
              color='warning'
              label={`Total filtrado: ${formatCurrency(totalValue)}`}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <Autocomplete
              size='small'
              options={eventFilterOptions}
              value={eventFilter}
              onChange={(_e, value) => setEventFilter(value)}
              onInputChange={(_e, value, reason) => {
                if (reason === 'input') setEventSearch(value)
                else if (reason === 'clear' || reason === 'reset') setEventSearch('')
              }}
              // Busca é server-side: não deixa o MUI re-filtrar a lista pelo texto do input.
              filterOptions={(x) => x}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params} placeholder='Filtrar por evento' />
              )}
              sx={{ width: { xs: 1, sm: 230 } }}
            />
            <TextField
              select
              size='small'
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              sx={{ width: { xs: 1, sm: 160 } }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value=''>Todas categorias</MenuItem>
              {Object.entries(COST_CATEGORY_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ width: { xs: 1, sm: 230 } }}>
              <DateTimeRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={(start, end) => {
                  setStartDate(start)
                  setEndDate(end)
                }}
                size='small'
                label=''
                placeholder='Período'
              />
            </Box>
            <Button
              size='small'
              variant='contained'
              startIcon={<Iconify icon='mingcute:add-line' width={16} />}
              onClick={openCreate}
              sx={{ whiteSpace: 'nowrap', px: 2 }}
            >
              Lançar custo
            </Button>
          </Stack>
        </Stack>

        {isLoading.value ? (
          <Box sx={{ p: 2.5 }}>
            <Skeleton variant='rounded' height={240} />
          </Box>
        ) : (
          <TableContainer>
            <Scrollbar>
              <Table size='small' sx={{ minWidth: 780 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Evento</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell align='right'>Data</TableCell>
                    <TableCell align='right'>Valor</TableCell>
                    <TableCell align='right' />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {costs.map((cost) => (
                    <TableRow key={cost._id} hover>
                      <TableCell>
                        <Typography variant='body2' fontWeight={600}>
                          {cost.description}
                        </Typography>
                        {cost.notes && (
                          <Typography variant='caption' color='text.secondary'>
                            {cost.notes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {cost.event ? (
                          <Typography variant='caption'>{cost.event.name}</Typography>
                        ) : (
                          <Chip size='small' variant='soft' label='Geral' />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size='small'
                          variant='soft'
                          color={cost.category === 'machine' ? 'info' : 'default'}
                          label={COST_CATEGORY_LABELS[cost.category] || cost.category}
                        />
                      </TableCell>
                      <TableCell align='right' sx={{ color: 'text.secondary' }}>
                        {fmtDate(cost.date)}
                      </TableCell>
                      <TableCell align='right'>
                        <Typography variant='body2' fontWeight={700} color='error.main'>
                          {formatCurrency(cost.value)}
                        </Typography>
                      </TableCell>
                      <TableCell align='right'>
                        <Tooltip title='Editar'>
                          <IconButton size='small' onClick={() => openEdit(cost)}>
                            <Iconify icon='solar:pen-bold' width={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Remover'>
                          <span>
                            <IconButton
                              size='small'
                              color='error'
                              disabled={deletingId === cost._id}
                              onClick={() => handleDelete(cost)}
                            >
                              <Iconify icon='solar:trash-bin-trash-bold' width={16} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!costs.length && (
                    <TableRow>
                      <TableCell colSpan={6} align='center' sx={{ py: 6 }}>
                        <Typography variant='body2' color='text.secondary'>
                          Nenhum custo lançado. Registre custos por evento ou custos gerais da
                          operação.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>
        )}

        <TablePagination
          component='div'
          count={total}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(e) => {
            setPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage='Custos por página'
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Card>

      <Dialog open={!!form} onClose={() => setForm(null)} fullWidth maxWidth='sm'>
        <DialogTitle>{editing ? 'Editar custo' : 'Lançar custo'}</DialogTitle>
        {form && (
          <DialogContent sx={{ pt: 1 }}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label='Descrição'
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                fullWidth
                size='small'
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label='Valor'
                  value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                  size='small'
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position='start'>R$</InputAdornment>,
                  }}
                />
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                  <DatePicker
                    label='Data'
                    value={form.date}
                    onChange={(value) => setForm({ ...form, date: value })}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Stack>
              <TextField
                select
                label='Categoria'
                value={form.category}
                onChange={(e) =>
                  setForm({ ...form, category: e.target.value as FinanceCostCategory })
                }
                size='small'
                fullWidth
              >
                {Object.entries(COST_CATEGORY_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <Autocomplete
                size='small'
                options={eventOptions}
                value={form.event}
                onChange={(_e, value) => setForm({ ...form, event: value })}
                onInputChange={(_e, value, reason) => {
                  if (reason === 'input') setEventSearch(value)
                  else if (reason === 'clear' || reason === 'reset') setEventSearch('')
                }}
                filterOptions={(x) => x}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Evento (opcional)'
                    helperText='Sem evento, o custo entra como custo geral no consolidado.'
                  />
                )}
              />
              <TextField
                label='Observações'
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                size='small'
                fullWidth
                multiline
                minRows={2}
              />
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button color='inherit' onClick={() => setForm(null)}>
            Cancelar
          </Button>
          <Button variant='contained' onClick={handleSave} disabled={isSaving.value}>
            {isSaving.value ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
